import './VisitTag.scss';

import * as React from 'react';

import { Visit } from '../../redux/visits/types';

import { RootState } from '../../redux/types';
import { useSelector } from 'react-redux';
import { TooltipHost } from '@fluentui/react';
import { css } from '../../util/Css';

const PCP_PURPLE_STATUSES = ['ATTRIBUTED_BY_HEAL', 'REQUEST_SENT_TO_PAYER', 'REQUEST_ACKNOWLEDGED_BY_PAYER'];

type Props = {
  visit: Visit;
};

export function VisitTag({ visit }: Props) {
  const agencies = useSelector((state: RootState) => state.concierge.agencies || []);

  const build = (content: string, tooltip: string, className: string, key: number) => {
    if (tooltip) {
      return (
        <TooltipHost key={key} content={tooltip}>
          <div className={css('label-as-badge', className)}>{content}</div>
        </TooltipHost>
      );
    } else {
      return (
        <div key={key} className={css('label-as-badge', className)}>
          {content}
        </div>
      );
    }
  };

  let index = 0;
  const tags: JSX.Element[] = [];

  if (visit.patientCompletedVisits === 0) {
    tags.push(build('First Visit', "This is the patient's first visit", 'orange-badge', index++));
  }

  if (visit.appliedSubscriptionContractName != null) {
    tags.push(build(visit.appliedSubscriptionContractName, `The patient's ${visit.appliedSubscriptionContractName} subscription applied to this visit.`, 'blue-badge', index++));
  }

  if (visit.patient && visit.patient.tags && visit.patient.tags.tagNames && visit.patient.tags.tagNames.length > 0) {
    const { tagNames } = visit.patient.tags;
    if (tagNames.find((t) => t.toLowerCase().indexOf('clover') > -1)) {
      tags.push(build('Clover', 'This patient is Clover Health member', 'purple-badge', index++));
    }

    if (tagNames.find((t) => t.toLowerCase().indexOf('together') > -1)) {
      tags.push(build('Together', 'This patient is Heal Together member', 'purple-badge', index++));
    }

    if (tagNames.find((t) => t.toLowerCase().indexOf('i-snp') > -1)) {
      tags.push(build('I-SNP', 'This patient is an I-SNP patient', 'green-badge', index++));
    }
  }

  if (visit.insuranceName || visit.insuranceMemberId || visit.insuranceGroupId) {
    tags.push(build('Insured', `This patient is using insurance: ${visit.insuranceName}`, 'green-badge', index++));
  }

  if (visit.isNextVisit) {
    tags.push(build('Follow Up', 'This is a follow up visit', 'orange-badge', index++));
  }

  if (visit.attributedToConciergeAgencyId) {
    const agency = agencies.find((a) => a.id === visit.attributedToConciergeAgencyId);
    const name = agency?.name || 'Partner';
    if (name.toLowerCase().indexOf('humana') > -1) {
      tags.push(build('Humana', `Member booked by ${name} partner group`, 'humana-badge', index++));
    } else if (name.toLowerCase().indexOf('carenet') > -1) {
      tags.push(build('Carenet', `Member booked by ${name} partner group`, 'blue-badge', index++));
    } else {
      tags.push(build('Partner', `Member booked by ${name} partner group`, 'blue-badge', index++));
    }
  }

  if (PCP_PURPLE_STATUSES.indexOf(visit.patientPCPStatus) !== -1) {
    tags.push(build('PCP', "The patient's primary care provider (PCP) has been attributed by Heal Doctors", 'purple-badge', index++));
  } else if (visit.patientPCPStatus === 'ATTRIBUTED_BY_PAYER') {
    tags.push(build('PCP', `The patient's primary care provider (PCP) has been attributed by the payer (${visit.insuranceName})`, 'green-badge', index++));
  } else if (visit.isVisitPD) {
    tags.push(build('PD', "This is a visit with the patient's preferred provider", 'blue-badge', index++));
  }

  return <div>{tags}</div>;
}
